import React, { useEffect, useState } from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';
import { Button, Upload } from 'antd';
import { InboxOutlined } from '@ant-design/icons';
import { useIntl } from 'react-intl';
import DrawerModal from '../../shared/DrawerModal';
import {
  clearEditingClaim,
  closeModal,
  getIsVisibleModal,
  selectEditingClaim,
} from '../../../redux/store/ui/common';
import { uploadClaimImage } from '../../../redux/store/userData/claims';
import { colors } from '../../../theme/theme';
import styles from './ClaimImageAddModal.styles';

const { Dragger } = Upload;

const ClaimImageAddModal: React.FC = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const visible = useSelector(getIsVisibleModal('ClaimImageAddModal'));
  const editingClaim = useSelector(selectEditingClaim);
  const editingClaimData = editingClaim?.claim as any;
  const editingSource = editingClaim?.source;

  const [fileList, setFileList] = useState<any[]>([]);
  const [submitting, setSubmitting] = useState<boolean>(false);

  useEffect(() => {
    if (visible) {
      setFileList([]);
      setSubmitting(false);
    }
  }, [visible]);

  const handleClose = () => {
    batch(() => {
      dispatch(closeModal('ClaimImageAddModal'));
      dispatch(clearEditingClaim());
    });
  };

  const handleSubmit = () => {
    if (fileList.length === 0 || !editingClaimData?.id) return;
    const file = fileList[0].originFileObj;
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async () => {
      setSubmitting(true);
      await dispatch(
        uploadClaimImage(
          editingClaimData.id,
          reader.result as string,
          editingSource ?? 'claim-drawer'
        )
      );
      setSubmitting(false);
      handleClose();
    };
    reader.readAsDataURL(file);
  };

  const uploadProps = {
    name: 'file',
    accept: 'image/*',
    fileList,
    multiple: false,
    customRequest: ({ onSuccess }: any) => {
      onSuccess();
    },
    showUploadList: false,
    onChange(info: any) {
      setFileList(info.fileList.filter((f: any) => !!f.status).slice(-1));
    },
  };

  const selectedFile = fileList[0];
  const removeSelectedFile = () => setFileList([]);

  const submitDisabled = fileList.length === 0 || submitting;

  return (
    <>
      <DrawerModal visible={visible} onClose={handleClose}>
        <div
          data-testid="ClaimImageAddModal-contents"
          className="claim-image-add-contents"
        >
          <div className="claim-image-add-title">
            {intl.formatMessage({ id: 'fleetadmin.claim.timeline.add' })}
          </div>
          <Dragger {...uploadProps}>
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">
              {intl.formatMessage({
                id: 'fleetadmin.claim.timeline.doc.title',
              })}
            </p>
            <p className="ant-upload-hint">
              {intl.formatMessage({
                id: 'fleetadmin.claim.timeline.doc.description',
              })}
            </p>
          </Dragger>
          {selectedFile && (
            <div className="claim-image-file-row">
              <span className="claim-image-file-name">
                📎 {selectedFile.name}
              </span>
              <span
                className="claim-image-file-remove"
                onClick={removeSelectedFile}
              >
                ✕
              </span>
            </div>
          )}
          <Button
            data-testid="ClaimImageAddModal-submitBtn"
            className="submit-button"
            onClick={handleSubmit}
            disabled={submitDisabled}
            loading={submitting}
            style={
              fileList.length === 0 ? { background: colors.buttonDisabled } : {}
            }
          >
            {intl.formatMessage({ id: 'submit' })}
          </Button>
          <Button className="cancel-button" onClick={handleClose}>
            {intl.formatMessage({ id: 'cancel' })}
          </Button>
        </div>
      </DrawerModal>
      <style jsx global>
        {styles}
      </style>
    </>
  );
};

export default ClaimImageAddModal;
